var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Stack, Box, Grid, useTheme, useMediaQuery, MenuItem, } from "@mui/material";
import { SMenu } from "@ui/components/menu/SMenu";
import { SButton } from "../../../components/buttons/SButton";
import { ArrowDown2 } from "iconsax-react";
import Menu from "@mui/material/Menu";
import { Icon } from "@iconify/react";
import { ReportCard } from "./ReportCard";
import { useTranslation } from "react-i18next";
import { ReportsTemperatureGrid } from "./ReportsTemperatureGrid";
import { userService } from "@utility/user-service";
import { DEFAULT_REPORT_SENSOR } from "../reportSensors";
/**
 * Format temperature value for display
 */
const formatTemp = (value) => {
    if (value === null || value === undefined)
        return "-";
    return value.toFixed(1);
};
export const TemperatureTable = ({ selectedNodes, gridColumnNodes, temperatureData, onDownloadExcel, onDownloadPdf, sensorMeta = DEFAULT_REPORT_SENSOR, noNodeHasSensor = false, }) => {
    const { t } = useTranslation(["common"]);
    const isSuperAdmin = userService.hasRole("Super Admin");
    const columnNodes = gridColumnNodes && gridColumnNodes.length > 0
        ? gridColumnNodes
        : selectedNodes;
    // Sorting menu state
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [sortField, setSortField] = useState("period");
    const [sortDirection, setSortDirection] = useState("asc");
    const sortMenuOpen = Boolean(sortAnchorEl);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Calculate table height: 100vh - (header + filters + title section + padding)
    const tableHeight = isMobile
        ? "calc(100vh - 180px)" // Mobile: accounting for collapsed header
        : "calc(100vh - 390px)"; // Desktop: accounting for full header
    const stickyLayers = {
        bodyStickyColumn: 10,
        subHeaderRow: 20,
        headerRow: 30,
        headerStickyCorner: 40,
    };
    const handleSortClick = (event) => {
        setSortAnchorEl(event.currentTarget);
    };
    const handleSortClose = () => {
        setSortAnchorEl(null);
    };
    const [action, setAction] = useState(isSuperAdmin ? "excel" : "pdf");
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const handleDownloadExcel = () => __awaiter(void 0, void 0, void 0, function* () {
        setDownloading(true);
        try {
            yield onDownloadExcel();
        }
        finally {
            setDownloading(false);
        }
    });
    const handleDownloadPdf = () => __awaiter(void 0, void 0, void 0, function* () {
        setDownloading(true);
        try {
            yield onDownloadPdf();
        }
        finally {
            setDownloading(false);
        }
    });
    const handleMainAction = () => {
        if (action === "excel") {
            handleDownloadExcel();
        }
        else {
            handleDownloadPdf();
        }
    };
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleActionSelect = (selected) => {
        setAction(selected);
        handleMenuClose();
        // Trigger the corresponding action immediately
        if (selected === "excel") {
            handleDownloadExcel();
        }
        else if (selected === "pdf") {
            handleDownloadPdf();
        }
    };
    // Helper function to get min/max/avg temperature across all selected APs for a row
    const getAggregatedTemp = (row, tempType) => {
        const values = [];
        columnNodes.forEach((ap) => {
            const apData = row[String(ap.value)];
            if (apData &&
                apData[tempType] !== null &&
                apData[tempType] !== undefined) {
                values.push(apData[tempType]);
            }
        });
        if (values.length === 0)
            return null;
        if (tempType === "minTemp")
            return Math.min(...values);
        if (tempType === "maxTemp")
            return Math.max(...values);
        return values.reduce((a, b) => a + b, 0) / values.length; // avg
    };
    // Sorted data using useMemo for performance
    const sortedData = useMemo(() => {
        if (!temperatureData.length)
            return temperatureData;
        return [...temperatureData].sort((a, b) => {
            let comparison = 0;
            if (sortField === "period") {
                // Sort by period start date
                comparison =
                    new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime();
            }
            else {
                // Sort by temperature values (minTemp, maxTemp, avgTemp)
                const aValue = getAggregatedTemp(a, sortField);
                const bValue = getAggregatedTemp(b, sortField);
                // Handle null values - push them to the end
                if (aValue === null && bValue === null)
                    return 0;
                if (aValue === null)
                    return 1;
                if (bValue === null)
                    return -1;
                comparison = aValue - bValue;
            }
            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [temperatureData, sortField, sortDirection, columnNodes]);
    // Handle sort selection
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        }
        else {
            setSortField(field);
            setSortDirection("asc");
        }
        handleSortClose();
    };
    // Get sort label with direction indicator
    const getSortLabel = (field, label) => {
        if (sortField === field) {
            return `${label} ${sortDirection === "asc" ? "↑" : "↓"}`;
        }
        return label;
    };
    // Sorting options
    const sortOptions = [
        {
            label: getSortLabel("period", "Sort by Period"),
            onClick: () => handleSort("period"),
        },
        {
            label: getSortLabel("minTemp", "Sort by Min Temp"),
            onClick: () => handleSort("minTemp"),
        },
        {
            label: getSortLabel("maxTemp", "Sort by Max Temp"),
            onClick: () => handleSort("maxTemp"),
        },
        {
            label: getSortLabel("avgTemp", "Sort by Avg Temp"),
            onClick: () => handleSort("avgTemp"),
        },
    ];
    return (React.createElement(Box, null,
        React.createElement(Stack, { direction: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, sx: { mb: 1.5, mt: { xs: 1.5, sm: 4 } } },
            React.createElement(Typography, { variant: "h4", sx: { color: "primary.darker" } },
                sensorMeta.name,
                " Per Period Data"),
            React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                React.createElement(SButton, { variant: "soft", sx: { fontWeight: 400 }, color: "inherit", size: "large", onClick: handleSortClick, endIcon: React.createElement(Icon, { icon: "hugeicons:arrow-data-transfer-vertical" }) }, "Sort"),
                React.createElement(SMenu, { anchorEl: sortAnchorEl, open: sortMenuOpen, handleClose: handleSortClose, menuItems: sortOptions }),
                React.createElement(Box, { display: "flex" },
                    React.createElement(SButton, { variant: "outlined", fullWidth: isMobile, onClick: handleMenuOpen, size: "large", disabled: downloading || selectedNodes.length === 0, endIcon: React.createElement(ArrowDown2, { size: 16 }), sx: { justifyContent: "space-between", minWidth: 180 } }, downloading
                        ? "Processing..."
                        : action === "excel"
                            ? "Download as Excel"
                            : "Download as PDF")),
                React.createElement(Menu, { anchorEl: anchorEl, open: menuOpen, onClose: handleMenuClose },
                    isSuperAdmin && (React.createElement(MenuItem, { onClick: () => handleActionSelect("excel") }, "Download as Excel")),
                    React.createElement(MenuItem, { onClick: () => handleActionSelect("pdf") }, "Download as PDF")))),
        selectedNodes.length === 0 ? (React.createElement(TableContainer, { sx: {
                overflowX: "auto",
                maxWidth: "100%",
                height: tableHeight,
                maxHeight: tableHeight,
                // border: `1px solid #919EAB1F`,
            } },
            React.createElement(Table, null,
                React.createElement(TableHead, { sx: { backgroundColor: theme.palette.common.white } },
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { sx: {
                                color: "text.secondary",
                                minWidth: 100,
                                borderTopLeftRadius: "50px",
                            } }, "Period"),
                        React.createElement(TableCell, { align: "center", sx: {
                                fontWeight: "medium",
                                fontSize: "0.875rem",
                                minWidth: 100,
                            } }, "Min"),
                        React.createElement(TableCell, { align: "center", sx: {
                                fontWeight: "medium",
                                fontSize: "0.875rem",
                                minWidth: 100,
                            } }, "Avg"),
                        React.createElement(TableCell, { align: "center", sx: {
                                fontWeight: "medium",
                                fontSize: "0.875rem",
                                minWidth: 100,
                                borderTopRightRadius: "50px",
                            } }, "Max"))),
                React.createElement(TableBody, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: 4, align: "center", sx: { py: 8 } },
                            React.createElement(Typography, { variant: "body1", color: "text.secondary" }, noNodeHasSensor
                                ? t("common:pages.reportsNoNodeHasSensor")
                                : t("common:pages.reportsSelectNodeDuration")))))))) : temperatureData.length === 0 ? (React.createElement(TableContainer, { sx: {
                overflowX: "auto",
                maxWidth: "100%",
                height: tableHeight,
                maxHeight: tableHeight,
            } },
            React.createElement(Table, { sx: {
                    minWidth: columnNodes.length * 300 + 200,
                    borderCollapse: "separate",
                    borderSpacing: 0,
                } },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { sx: {
                                position: "sticky",
                                left: 0,
                                zIndex: stickyLayers.headerStickyCorner,
                                minWidth: 130,
                                backgroundColor: theme.palette.background.neutral,
                            } }),
                        columnNodes.map((ap) => (React.createElement(TableCell, { key: String(ap.value), align: "center", colSpan: 3, sx: {
                                backgroundColor: "#DCD7FF !important",
                                fontWeight: "bold",
                                color: "text.secondary",
                                minWidth: 300,
                            } }, ap.label))))),
                React.createElement(TableBody, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: 1 + columnNodes.length * 3, align: "center", sx: { py: 8 } },
                            React.createElement(Typography, { variant: "body1", color: "text.secondary" }, "No data available for the selected duration"))))))) : isMobile ? (React.createElement(Grid, { container: true, spacing: 1.5 }, sortedData.map((row, index) => (React.createElement(Grid, { key: index, size: { xs: 12, sm: 6 } },
            React.createElement(ReportCard, { row: row, selectedNodes: columnNodes, sensorMeta: sensorMeta })))))) : (React.createElement(ReportsTemperatureGrid, { selectedNodes: columnNodes, rows: sortedData, tableHeight: tableHeight, sensorMeta: sensorMeta }))));
};
